import { useMemo } from 'react';

import { BinderFolder, useBinderFolders } from '@/hooks/useBinderFolders';

/**
 * The 130 binder folders as combobox options, labelled `CODE · Name`.
 *
 * The combobox filters on the label, so carrying both halves is what makes a
 * folder findable by code *and* by name from one input.
 */
export function folderOptions(folders: BinderFolder[]) {
    return folders.map((f) => ({
        value: f.code,
        label: `${f.code} · ${f.name}`,
    }));
}

/**
 * Shown in the folder combobox when the taxonomy itself is missing, in place of
 * the generic "No results found."
 *
 * That default reads as "this binder has no such folder", which is never true —
 * the taxonomy is a checked-in file of 130 codes, so an empty list can only mean
 * the request failed. Saying so stops an annotator hunting for a folder that
 * exists. The folder is optional, so a failed load never blocks saving.
 */
export const FOLDER_LOAD_FAILED_MESSAGE =
    "Couldn't load the binder folders.";

/**
 * The combobox's empty-state message, or `undefined` to keep its own default.
 *
 * Keyed off the resolved option count rather than the query's `isError`, so a
 * failed background refetch that still has the 130 folders cached keeps the
 * ordinary message — the picker works, and only a genuinely empty list is worth
 * shouting about.
 */
export function folderEmptyMessage(
    optionCount: number,
    isLoading: boolean,
): string | undefined {
    return optionCount === 0 && !isLoading
        ? FOLDER_LOAD_FAILED_MESSAGE
        : undefined;
}

export function useFolderOptions() {
    const { data: folders, ...rest } = useBinderFolders();

    const data = useMemo(() => folderOptions(folders ?? []), [folders]);

    return {
        data,
        emptyMessage: folderEmptyMessage(data.length, rest.isLoading),
        ...rest,
    };
}
